"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DatePickerFrame = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _web = require("@react-hookz/web");
var _lodestarIcons = require("@smartsheet/lodestar-icons");
var _ursaIcons = require("@smartsheet/ursa-icons");
var _add = _interopRequireDefault(require("date-fns/add"));
var _eachDayOfInterval = _interopRequireDefault(require("date-fns/eachDayOfInterval"));
var _eachWeekOfInterval = _interopRequireDefault(require("date-fns/eachWeekOfInterval"));
var _endOfMonth = _interopRequireDefault(require("date-fns/endOfMonth"));
var _endOfWeek = _interopRequireDefault(require("date-fns/endOfWeek"));
var _isSameMonth = _interopRequireDefault(require("date-fns/isSameMonth"));
var _isToday = _interopRequireDefault(require("date-fns/isToday"));
var _setYear = _interopRequireDefault(require("date-fns/setYear"));
var _startOfMonth = _interopRequireDefault(require("date-fns/startOfMonth"));
var _startOfWeek = _interopRequireDefault(require("date-fns/startOfWeek"));
var _sub = _interopRequireDefault(require("date-fns/sub"));
var _react = _interopRequireWildcard(require("react"));
var _reactFocusLock = _interopRequireDefault(require("react-focus-lock"));
var _i18n = require("../../../../i18n/i18n");
var _useTheme = require("../../../../theme/utils/useTheme");
var _calendar = require("../../util/calendar");
var _DatePickerDayBox = require("../styled/DatePickerDayBox");
var _DatePickerDayContainer = require("../styled/DatePickerDayContainer");
var _DatePickerDayOfWeek = require("../styled/DatePickerDayOfWeek");
var _DatePickerDialogMonthContainer = require("../styled/DatePickerDialogMonthContainer");
var _DatePickerDialogStyled = require("../styled/DatePickerDialogStyled");
var _DatePickerFooter = require("../styled/DatePickerFooter");
var _DatePickerFooterButton = require("../styled/DatePickerFooterButton");
var _DatePickerMonthName = require("../styled/DatePickerMonthName");
var _DatePickerMonthSwitchButton = require("../styled/DatePickerMonthSwitchButton");
var _datePickerTheme = require("../styled/datePickerTheme");
var _DayTracker = require("../util/DayTracker");
var _datePickerFrameUtil = require("../util/datePickerFrameUtil");
var _DatePickerYearSelector = require("./DatePickerYearSelector");
const DatePickerFrame = /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  let {
    activeDate,
    activeMonth,
    applyIsDisabled,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    children,
    className,
    clientId,
    dayContainerRef,
    dayIsDisabled,
    id,
    locale,
    maxDate,
    minDate,
    onApplyClicked,
    onClearClicked,
    onCloseRequested,
    onSpaceOrEnter,
    onTodayClicked,
    requestDateChange
  } = _ref;
  const {
    i18n
  } = (0, _i18n.useI18n)();
  const {
    current: dayTracker
  } = (0, _react.useRef)(new _DayTracker.DayTracker());
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;

  // getMonthMatrix is not a slow operation, but memoizing it means the dates it returns are
  // stable across renders which helps React out a lot.
  const stableMonthMatrixRef = (0, _react.useRef)([]);
  const stableMonthMatrixMonth = (0, _react.useRef)(activeMonth);
  const monthMatrix = (0, _react.useMemo)(() => {
    // If the active month has changed, we need to recompute the matrix and store it into
    // stableMonthMatrixRef. Otherwise, return the existing stableMonthMatrixRef's value.
    if (!stableMonthMatrixRef.current.length || !(0, _isSameMonth.default)(stableMonthMatrixMonth.current, activeMonth)) {
      /**
       * Adapted from https://gist.github.com/miljan-aleksic/bd70452a3f0cd6a11545db9f6ab57df6
       */
      const matrix = (0, _eachWeekOfInterval.default)({
        start: (0, _startOfMonth.default)(activeMonth),
        end: (0, _endOfMonth.default)(activeMonth)
      });
      stableMonthMatrixRef.current = matrix.flatMap(weekDay => (0, _eachDayOfInterval.default)({
        start: (0, _startOfWeek.default)(weekDay),
        end: (0, _endOfWeek.default)(weekDay)
      }));
      stableMonthMatrixMonth.current = activeMonth;
    }
    return stableMonthMatrixRef.current;
  }, [activeMonth]);
  const dayIndices = [0, 1, 2, 3, 4, 5, 6];
  const daySetterRef = (dayBtn, date) => {
    if (!dayBtn) {
      dayTracker.setDate(date, null);
    } else {
      dayTracker.setDate(date, dayBtn);
    }
  };
  const getDayLabel = dayIndex => {
    const dayDate = new Date(2023, 0, 1 + dayIndex % 7); // Jan 01, 2023 was a Sunday
    const getFormattedLabel = type => {
      // Get the applicable localized string and strip any periods from the end of it
      return dayDate.toLocaleDateString(locale, {
        weekday: type
      }).replace(/\./g, "");
    };
    // TODO Move this into datePickerTheme
    switch (themeMode) {
      case "ursa-light":
        return getFormattedLabel("short");
      case "lodestar-light":
      default:
        return getFormattedLabel("narrow");
    }
  };
  const handleEscapeOnKeyDown = event => {
    switch (event.key) {
      case "Escape":
        onCloseRequested(true);
        break;
    }
  };
  const prevMonthEnabled = (0, _datePickerFrameUtil.canMoveToPreviousMonth)(activeDate, minDate);
  const nextMonthEnabled = (0, _datePickerFrameUtil.canMoveToNextMonth)(activeDate, maxDate);
  const handleDayKeyDown = event => {
    if (event.altKey || event.shiftKey || event.ctrlKey || event.metaKey) {
      return;
    }
    let newDate = null;
    switch (event.key) {
      case "Enter":
      case " ":
        if (!(0, _calendar.isDayDisabled)(activeDate, dayIsDisabled, minDate, maxDate)) {
          // Don't let this propagate to a click later on, we
          // don't want to double-handle this event.
          event.preventDefault();
          onSpaceOrEnter?.();
        }
        return;
      case "ArrowUp":
        newDate = (0, _sub.default)(activeDate, {
          weeks: 1
        });
        break;
      case "ArrowDown":
        newDate = (0, _add.default)(activeDate, {
          weeks: 1
        });
        break;
      case "ArrowLeft":
        newDate = (0, _sub.default)(activeDate, {
          days: 1
        });
        break;
      case "ArrowRight":
        newDate = (0, _add.default)(activeDate, {
          days: 1
        });
        break;
      case "Home":
        newDate = (0, _sub.default)(activeDate, {
          years: 1
        });
        break;
      case "End":
        newDate = (0, _add.default)(activeDate, {
          years: 1
        });
        break;
      case "PageDown":
        newDate = (0, _add.default)(activeDate, {
          months: 1
        });
        break;
      case "PageUp":
        newDate = (0, _sub.default)(activeDate, {
          months: 1
        });
        break;
    }
    if (newDate) {
      // If yearRange is provided, return early if the new date is out of that range.
      if (minDate && newDate.getFullYear() < minDate.year) {
        // TODO we should notify via a live region that the requested move is out of bounds.
        return;
      } else if (maxDate && newDate.getFullYear() > maxDate.year) {
        // TODO we should notify via a live region that the requested move is out of bounds.
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      requestDateChange(newDate);
      const finalNewDate = newDate;
      requestAnimationFrame(() => {
        const newBtn = dayTracker.getDate(finalNewDate);
        if (newBtn) {
          newBtn.focus();
        }
      });
    }
  };

  // Set up the initial focused element on mount.
  (0, _web.useMountEffect)(() => {
    const initialDateBtn = dayTracker.getDate(activeDate);
    if (initialDateBtn) {
      initialDateBtn.focus();
    }
  });
  return /*#__PURE__*/_react.default.createElement(_reactFocusLock.default
  // TODO: add tests to ensure this is functioning correctly, need to ignore TS error because of ReactFocusLock API
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  , {
    as: _DatePickerDialogStyled.DatePickerDialogStyled,
    ref: ref,
    className: className
    // Naw, we know what we're doing :)
    // eslint-disable-next-line jsx-a11y/no-autofocus
    ,
    autoFocus: false,
    lockProps: {
      onKeyDown: handleEscapeOnKeyDown,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledby,
      "data-client-id": clientId,
      // Using role="application" here because otherwise some screen readers will
      // override the arrow buttons to make up down navigation through buttons in order.
      // further, JAWS REALLY has trouble with the focus moving in and out of this dialog,
      // so we are just going to manage everything inside here.
      role: "application",
      id,
      themeMode
    }
  }, /*#__PURE__*/_react.default.createElement(_DatePickerDialogMonthContainer.DatePickerDialogMonthContainer, null, /*#__PURE__*/_react.default.createElement(_DatePickerMonthSwitchButton.DatePickerMonthSwitchButton, {
    themeMode: themeMode,
    size: "medium",
    appearance: _datePickerTheme.datePickerTheme.topButtonType[themeMode],
    "aria-label": i18n._( /*i18n*/{
      id: "Show previous month",
      comment: "The aria label for the button that goes back one month."
    }),
    onClick: () => {
      requestDateChange((0, _sub.default)(activeDate, {
        months: 1
      }));
    },
    iconBefore: arrowIconsForTheme[themeMode].left,
    isDisabled: !prevMonthEnabled
  }), /*#__PURE__*/_react.default.createElement(_DatePickerMonthSwitchButton.DatePickerMonthSwitchButton, {
    themeMode: themeMode,
    size: "medium",
    appearance: _datePickerTheme.datePickerTheme.topButtonType[themeMode],
    "aria-label": i18n._( /*i18n*/{
      id: "Show next month",
      comment: "The aria label for the button that goes forward one month."
    }),
    onClick: () => {
      requestDateChange((0, _add.default)(activeDate, {
        months: 1
      }));
    },
    iconBefore: arrowIconsForTheme[themeMode].right,
    isDisabled: !nextMonthEnabled
  }), /*#__PURE__*/_react.default.createElement(_DatePickerMonthName.DatePickerMonthName, (0, _extends2.default)({}, (0, _datePickerTheme.getTypeRampPropsMonthYear)(themeMode), {
    "aria-live": "polite",
    renderAs: "div",
    omitTooltip: true
  }), activeMonth.toLocaleDateString(locale, {
    month: "long"
  })), /*#__PURE__*/_react.default.createElement(_DatePickerYearSelector.DatePickerYearSelector, {
    maxDate: maxDate,
    minDate: minDate,
    selectedYear: activeMonth.getFullYear(),
    locale: locale,
    onChange: newYear => {
      requestDateChange((0, _setYear.default)(activeDate, newYear));
    }
  })), /*#__PURE__*/_react.default.createElement(_DatePickerDayContainer.DatePickerDayContainer, {
    ref: dayContainerRef,
    themeMode: themeMode
  }, dayIndices.map(i => /*#__PURE__*/_react.default.createElement(_DatePickerDayOfWeek.DatePickerDayOfWeek, (0, _extends2.default)({}, (0, _datePickerTheme.getTypeRampPropsDayOfWeek)(themeMode), {
    key: i,
    themeMode: themeMode
  }), getDayLabel(i))), monthMatrix.map(day => {
    if (!(0, _isSameMonth.default)(day, activeMonth)) {
      // Omit the days from other months from the grid.
      // Add a spacer element to align everything properly.
      return /*#__PURE__*/_react.default.createElement(_DatePickerDayBox.DatePickerDayBox, {
        key: `${day.getMonth()}-${day.getDate()}`,
        themeMode: themeMode
      }, /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null));
    } else {
      return children({
        day,
        htmlContent: `${day.getDate()}`,
        isDisabled: (0, _calendar.isDayDisabled)(day, dayIsDisabled, minDate, maxDate),
        isToday: (0, _isToday.default)(day),
        key: `${day.getMonth()}-${day.getDate()}`,
        onKeyDown: handleDayKeyDown,
        ref: btn => daySetterRef(btn, day)
      });
    }
  })), /*#__PURE__*/_react.default.createElement(_DatePickerFooter.DatePickerFooter, {
    orientation: "row",
    space: "small",
    themeMode: themeMode
  }, /*#__PURE__*/_react.default.createElement(_DatePickerFooterButton.DatePickerFooterButton, {
    appearance: _datePickerTheme.datePickerTheme.footer.buttonAppearance.standard[themeMode],
    onClick: () => {
      if (process.env.NODE_ENV !== "production") {
        if (!onClearClicked) {
          console.warn("You have not provided an onClearClicked handler for the " + "DatePickerDialog. This means that the clear button will not " + "do anything when clicked.");
        }
      }
      onClearClicked?.();
    }
  }, i18n._( /*i18n*/{
    id: "Clear",
    comment: "The text for a button that will clear out the value of a date selection"
  })), onTodayClicked && /*#__PURE__*/_react.default.createElement(_DatePickerFooterButton.DatePickerFooterButton, {
    appearance: _datePickerTheme.datePickerTheme.footer.buttonAppearance.standard[themeMode],
    onClick: () => {
      if (process.env.NODE_ENV !== "production") {
        if (!onClearClicked) {
          console.warn("You have not provided an onClearClicked handler for the " + "DatePickerDialog. This means that the clear button will not " + "do anything when clicked.");
        }
      }
      onTodayClicked();
    }
  }, i18n._( /*i18n*/{
    id: "Today",
    comment: "The text shown in a button that will select today's date in a date picker."
  })), onApplyClicked && /*#__PURE__*/_react.default.createElement(_DatePickerFooterButton.DatePickerFooterButton, {
    appearance: _datePickerTheme.datePickerTheme.footer.buttonAppearance.apply[themeMode],
    isDisabled: applyIsDisabled,
    onClick: onApplyClicked
  }, i18n._( /*i18n*/{
    id: "Apply",
    comment: "The text for the button that will apply the currently selected changes."
  }))));
});
exports.DatePickerFrame = DatePickerFrame;
DatePickerFrame.displayName = "DatePickerFrame";
const arrowIconsForTheme = {
  "ursa-light": {
    // These icons have special color requirements that we'll set at the parent Button.
    left: /*#__PURE__*/_react.default.createElement(_ursaIcons.ChevronLeftIcon, {
      size: 24,
      color: "inherit"
    }),
    right: /*#__PURE__*/_react.default.createElement(_ursaIcons.ChevronRightIcon, {
      size: 24,
      color: "inherit"
    })
  },
  "lodestar-light": {
    left: /*#__PURE__*/_react.default.createElement(_lodestarIcons.ArrowLeftIcon, {
      size: "medium"
    }),
    right: /*#__PURE__*/_react.default.createElement(_lodestarIcons.ArrowRightIcon, {
      size: "medium"
    })
  },
  oldestar: {
    left: /*#__PURE__*/_react.default.createElement(_lodestarIcons.ArrowLeftIcon, {
      size: "medium"
    }),
    right: /*#__PURE__*/_react.default.createElement(_lodestarIcons.ArrowRightIcon, {
      size: "medium"
    })
  }
};
//# sourceMappingURL=DatePickerFrame.js.map